import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Row, Col, Card, Skeleton, Typography, Space } from 'antd';
import { PlusOutlined, DownloadOutlined, EyeOutlined, CalendarOutlined, FireOutlined, ClockCircleOutlined, FilePdfOutlined } from '@ant-design/icons';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import PdfModal from 'components/pdf-modal/pdf-modal';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { getDateFormat } from 'services/Utils';
import { useI18n } from 'hooks/useI18n';
import { translateWorkoutStatus } from 'helpers/workoutI18n';
import './workout-plan-modal.scss';

const { Text } = Typography;

const LOADING_PLACEHOLDER_ITEMS = [1, 2, 3];

const WorkoutPlanModal = ({ workoutPlanModal, selectedCustomer, onClose }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutPlanRedux = useSelector((state) => state.workoutPlanRedux);
	const loginRedux = useSelector((state) => state.loginRedux);
	const [actionLoading, setActionLoading] = useState({ planId: null, action: null });
	const [pdfPreview, setPdfPreview] = useState({
		visible: false,
		url: '',
		title: '',
		error: null,
	});

	const loading = globalRedux.apiStatus.GET_WORKOUT_PLANS === API_STATUS.PENDING;
	const plans = workoutPlanRedux?.customerPlans || [];

	const fetchPlans = useCallback(() => {
		if (selectedCustomer?.id) {
			dispatch(getApi('GET_WORKOUT_PLANS', `${SERVER_IP}api/workoutPlan/customer/${selectedCustomer.id}`));
		}
	}, [dispatch, selectedCustomer]);

	useEffect(() => {
		if (workoutPlanModal) {
			fetchPlans();
		}
	}, [workoutPlanModal, fetchPlans]);

	const revokeBlobUrl = (url) => {
		if (url?.startsWith('blob:')) {
			window.URL.revokeObjectURL(url);
		}
	};

	const getReportFileName = (responseHeaders, planId) => {
		const disposition = responseHeaders?.['content-disposition'] || responseHeaders?.['Content-Disposition'];
		const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
		if (utf8Match?.[1]) {
			return decodeURIComponent(utf8Match[1]);
		}

		const asciiMatch = disposition?.match(/filename="?([^";]+)"?/i);
		if (asciiMatch?.[1]) {
			return asciiMatch[1];
		}

		return `workout-plan-${planId}.pdf`;
	};

	const fetchPlanReport = async (planId) => {
		const response = await axios.get(`${SERVER_IP}api/workoutPlan/${planId}/download-report`, {
			responseType: 'blob',
			headers: { Authorization: `Bearer ${loginRedux.token}` },
		});

		const contentType = response.headers?.['content-type'];
		if (contentType && !contentType.includes('pdf')) {
			throw new Error('Invalid report response');
		}

		return {
			blob: response.data,
			fileName: getReportFileName(response.headers, planId),
		};
	};

	const handleOpenPdf = async (planId) => {
		try {
			setActionLoading({ planId, action: 'preview' });
			const { blob, fileName } = await fetchPlanReport(planId);
			revokeBlobUrl(pdfPreview.url);
			const previewUrl = window.URL.createObjectURL(blob);

			setPdfPreview({
				visible: true,
				url: previewUrl,
				title: fileName || t('workouts.plan.modal.pdf_title_fallback', { id: planId }),
				error: null,
			});
		} catch (error) {
			setPdfPreview({
				visible: true,
				url: '',
				title: t('workouts.plan.modal.pdf_preview_title'),
				error: t('workouts.plan.modal.load_pdf_failed'),
			});
			toast.error(t('workouts.plan.modal.load_pdf_failed'));
		} finally {
			setActionLoading({ planId: null, action: null });
		}
	};

	const handleDownloadPdf = async (planId) => {
		try {
			setActionLoading({ planId, action: 'download' });
			const { blob, fileName } = await fetchPlanReport(planId);
			const downloadUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = downloadUrl;
			link.download = fileName;
			link.style.display = 'none';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(downloadUrl);
			toast.success(t('workouts.plan.modal.download_success'));
		} catch (error) {
			toast.error(t('workouts.plan.modal.download_failed'));
		} finally {
			setActionLoading({ planId: null, action: null });
		}
	};

	const closePdfPreview = () => {
		revokeBlobUrl(pdfPreview.url);
		setPdfPreview({
			visible: false,
			url: '',
			title: '',
			error: null,
		});
	};

	const navigateTo = (path) => {
		onClose();
		navigate(path);
	};

	const activePlansCount = plans.filter((plan) => plan.status === 'active').length;
	const totalDuration = plans.reduce((sum, plan) => sum + (plan.durationDays || 0), 0);

	const modalTitle = (
		<div className="workout-plan-modal__title-block">
			<div className="workout-plan-modal__title-row">
				<div className="workout-plan-modal__title">
					{selectedCustomer?.customerName || t('workouts.plan.modal.title')}
				</div>
				<div className="workout-plan-modal__title-badge">{t('workouts.plan.modal.plans_badge', { count: plans.length })}</div>
			</div>
			<Text className="workout-plan-modal__title-subtitle">
				{plans.length > 0
					? t('workouts.plan.modal.assigned_summary', { count: plans.length })
					: t('workouts.plan.modal.manage_downloads')}
			</Text>
		</div>
	);

	return (
		<>
			<Modal
				title={modalTitle}
				open={workoutPlanModal}
				onCancel={onClose}
				className="workout-plan-modal"
				footer={
					<Button type="primary" onClick={onClose}>
						{t('common.close')}
					</Button>
				}
				width={960}
				centered
				styles={{ body: { padding: 20 } }}
				destroyOnClose>
				<Row>
				{loading ? (
					<Col span={24} className="workout-plan-modal__loading-shell">
						<section className="workout-plan-modal__hero workout-plan-modal__hero--loading">
							<div className="workout-plan-modal__hero-copy">
								<Skeleton.Button active size="small" className="workout-plan-modal__skeleton-eyebrow" />
								<Skeleton.Input active size="default" className="workout-plan-modal__skeleton-title" />
								<Skeleton active title={false} paragraph={{ rows: 2, width: ['96%', '72%'] }} />
							</div>
							<div className="workout-plan-modal__hero-stats">
								{LOADING_PLACEHOLDER_ITEMS.map((item) => (
									<div key={item} className="workout-plan-modal__stat-card workout-plan-modal__stat-card--loading">
										<Skeleton.Avatar active size={40} shape="circle" />
										<div className="workout-plan-modal__stat-card-content">
											<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-stat-value" />
											<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-stat-label" />
										</div>
									</div>
								))}
							</div>
						</section>
						<div className="workout-plan-modal__toolbar workout-plan-modal__toolbar--loading">
							<div className="workout-plan-modal__toolbar-copy">
								<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-toolbar-title" />
								<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-toolbar-subtitle" />
							</div>
							<Skeleton.Button active className="workout-plan-modal__skeleton-toolbar-action" />
						</div>
						<Row gutter={[18, 18]} className="workout-plan-modal__plans-grid">
							{LOADING_PLACEHOLDER_ITEMS.map((item) => (
								<Col key={item} span={24}>
									<Card bordered={false} className="workout-plan-modal__plan-card workout-plan-modal__plan-card--loading">
										<Row gutter={[16, 16]} align="middle" justify="space-between">
											<Col flex="auto">
												<Space direction="vertical" size={12} className="workout-plan-modal__plan-content">
													<Row justify="space-between" align="middle" gutter={[12, 12]}>
														<Col flex="auto">
															<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-plan-title" />
														</Col>
														<Col>
															<Skeleton.Button active size="small" className="workout-plan-modal__skeleton-status" />
														</Col>
													</Row>
													<div className="workout-plan-modal__meta-row">
														{LOADING_PLACEHOLDER_ITEMS.map((metaItem) => (
															<div key={metaItem} className="workout-plan-modal__meta-chip workout-plan-modal__meta-chip--loading">
																<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-meta-label" />
																<Skeleton.Input active size="small" className="workout-plan-modal__skeleton-meta-value" />
															</div>
														))}
													</div>
												</Space>
											</Col>
											<Col className="workout-plan-modal__actions-col">
												<div className="workout-plan-modal__actions">
													<Skeleton.Button active className="workout-plan-modal__skeleton-action-btn" />
													<Skeleton.Button active className="workout-plan-modal__skeleton-action-btn" />
													<Skeleton.Button active className="workout-plan-modal__skeleton-action-btn workout-plan-modal__skeleton-action-btn--primary" />
												</div>
											</Col>
										</Row>
									</Card>
								</Col>
							))}
						</Row>
					</Col>
				) : (
					<Col span={24}>
						<section className="workout-plan-modal__hero">
							<div className="workout-plan-modal__hero-copy">
								<div className="workout-plan-modal__eyebrow">{t('workouts.list.eyebrow')}</div>
								<h3>{t('workouts.plan.modal.hero_title')}</h3>
								<p>{t('workouts.plan.modal.hero_subtitle')}</p>
							</div>
							<div className="workout-plan-modal__hero-stats">
								<div className="workout-plan-modal__stat-card">
									<FireOutlined />
									<div>
										<span>{plans.length}</span>
										<label>{t('workouts.plan.modal.total_plans')}</label>
									</div>
								</div>
								<div className="workout-plan-modal__stat-card">
									<ClockCircleOutlined />
									<div>
										<span>{activePlansCount}</span>
										<label>{t('workouts.plan.modal.active_now')}</label>
									</div>
								</div>
								<div className="workout-plan-modal__stat-card">
									<CalendarOutlined />
									<div>
										<span>{totalDuration}</span>
										<label>{t('workouts.plan.modal.total_days')}</label>
									</div>
								</div>
							</div>
						</section>
						{plans.length > 0 && (
							<Row justify="space-between" align="middle" className="workout-plan-modal__toolbar">
								<Col>
									<Space direction="vertical" size={2}>
										<Text className="workout-plan-modal__toolbar-title">{t('workouts.plan.modal.assigned_plans')}</Text>
										<Text className="workout-plan-modal__toolbar-subtitle">{t('workouts.plan.modal.toolbar_subtitle')}</Text>
									</Space>
								</Col>
								<Col>
									<Button
										type="primary"
										icon={<PlusOutlined />}
										onClick={() => navigateTo(`/workout-plan/assign/${selectedCustomer?.id}`)}>
										{t('workouts.plan.modal.assign_new_plan')}
									</Button>
								</Col>
							</Row>
						)}
						{plans.length === 0 ? (
							<Row justify="center" className="workout-plan-modal__empty-row">
								<Col span={24}>
									<Card bordered={false} className="workout-plan-modal__empty-card">
										<Space direction="vertical" size={12} className="workout-plan-modal__empty-content">
											<div className="workout-plan-modal__empty-icon">
												<CalendarOutlined />
											</div>
											<Text className="workout-plan-modal__empty-title">{t('workouts.plan.modal.empty_title')}</Text>
											<Text className="workout-plan-modal__empty-description">{t('workouts.plan.modal.empty_description')}</Text>
											<div>
												<Button
													type="primary"
													size="large"
													icon={<PlusOutlined />}
													onClick={() => navigateTo(`/workout-plan/assign/${selectedCustomer?.id}`)}>
													{t('workouts.plan.assign.assign_plan')}
												</Button>
											</div>
										</Space>
									</Card>
								</Col>
							</Row>
						) : (
							<Row gutter={[18, 18]} className="workout-plan-modal__plans-grid">
								{plans.map((plan) => (
									<Col key={plan.id} span={24}>
										<Card
											bordered={false}
											className={`workout-plan-modal__plan-card workout-plan-modal__plan-card--${plan.status || 'default'}`}>
											<Row gutter={[16, 16]} align="middle" justify="space-between">
												<Col flex="auto">
													<Space direction="vertical" size={12} className="workout-plan-modal__plan-content">
														<Row justify="space-between" align="middle" gutter={[12, 12]}>
															<Col flex="auto">
																<Text className="workout-plan-modal__plan-title">{plan.name}</Text>
															</Col>
															<Col>
																<span className={`workout-plan-modal__status-pill workout-plan-modal__status-pill--${plan.status || 'default'}`}>
																	{translateWorkoutStatus(t, plan.status, plan.status)}
																</span>
															</Col>
														</Row>
														<div className="workout-plan-modal__meta-row">
															<div className="workout-plan-modal__meta-chip">
																<label>{t('workouts.plan.customer_list.start_label')}</label>
																<div>
																	{getDateFormat(plan.startDate, DATE_FORMAT.DD_MM_YYYY)}
																</div>
															</div>
															<div className="workout-plan-modal__meta-chip">
																<label>{t('workouts.plan.customer_list.end_label')}</label>
																<div>
																	{getDateFormat(plan.endDate, DATE_FORMAT.DD_MM_YYYY)}
																</div>
															</div>
															<div className="workout-plan-modal__meta-chip">
																<label>{t('workouts.plan.assign.duration')}</label>
																<div>{t('workouts.common.days_count', { count: plan.durationDays || 0 })}</div>
															</div>
														</div>
													</Space>
												</Col>
												<Col className="workout-plan-modal__actions-col">
													<div className="workout-plan-modal__actions">
														<Button
															className="workout-plan-modal__action-btn"
															icon={<FilePdfOutlined />}
															loading={actionLoading.planId === plan.id && actionLoading.action === 'preview'}
															disabled={actionLoading.planId === plan.id && actionLoading.action === 'preview'}
															onClick={() => handleOpenPdf(plan.id)}>
															{t('workouts.plan.modal.open_pdf')}
														</Button>
														<Button
															className="workout-plan-modal__action-btn"
															icon={<DownloadOutlined />}
															loading={actionLoading.planId === plan.id && actionLoading.action === 'download'}
															disabled={actionLoading.planId === plan.id && actionLoading.action === 'download'}
															onClick={() => handleDownloadPdf(plan.id)}>
															{t('workouts.template.pdf.download_pdf')}
														</Button>
														<Button
															type="primary"
															className="workout-plan-modal__action-btn workout-plan-modal__action-btn--primary"
															icon={<EyeOutlined />}
															onClick={() => navigateTo(`/workout-plan/${plan.id}`)}>
															{t('workouts.plan.customer_list.view_details')}
														</Button>
													</div>
												</Col>
											</Row>
										</Card>
									</Col>
								))}
							</Row>
						)}
					</Col>
				)}
				</Row>
			</Modal>
			<PdfModal
				visible={pdfPreview.visible}
				onClose={closePdfPreview}
				pdfUrl={pdfPreview.url}
				title={pdfPreview.title}
				loading={Boolean(actionLoading.planId && actionLoading.action === 'preview')}
				error={pdfPreview.error}
				width="92%"
				height="82vh"
			/>
		</>
	);
};

export default WorkoutPlanModal;
